import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Printer, RefreshCw, CheckCircle, XCircle, 
  AlertTriangle, Download, Settings, TestTube
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import thermalPrinter from '../services/ThermalPrinterService';

const PrinterConfigPage = () => {
  const [status, setStatus] = useState('checking'); // checking, connected, disconnected, error
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [loading, setLoading] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);

  useEffect(() => {
    checkConnection();
    // Mount-only initialization; checkConnection is stable inside this component
    // and only reads from refs/setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkConnection = async () => {
    setStatus('checking');
    setLoading(true);
    try {
      const available = await thermalPrinter.isAvailable();
      if (available) {
        setStatus('connected');
        await loadPrinters();
      } else {
        setStatus('disconnected');
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const loadPrinters = async () => {
    try {
      const printerList = await thermalPrinter.getPrinters();
      setPrinters(printerList);
      
      // Try to find default thermal printer
      const defaultPrinter = await thermalPrinter.findDefaultPrinter();
      if (defaultPrinter) {
        setSelectedPrinter(defaultPrinter);
      }
    } catch (error) {
      console.error('Error loading printers:', error);
      toast.error('Error al cargar impresoras');
    }
  };

  const handleSelectPrinter = (printer) => {
    setSelectedPrinter(printer);
    thermalPrinter.setPrinter(printer);
    toast.success(`Impresora seleccionada: ${printer}`);
  };

  const handleTestPrint = async () => {
    if (!selectedPrinter) {
      toast.error('Seleccione una impresora primero');
      return;
    }

    setTestPrinting(true);
    try {
      await thermalPrinter.printTestTicket();
      toast.success('Prueba de impresión enviada');
    } catch (error) {
      console.error('Error printing test:', error);
      toast.error('Error al imprimir prueba: ' + error.message);
    } finally {
      setTestPrinting(false);
    }
  };

  const getStatusInfo = () => {
    switch (status) {
      case 'connected':
        return {
          icon: CheckCircle,
          color: 'text-emerald-500',
          bg: 'bg-emerald-50',
          text: 'QZ Tray conectado',
          description: 'La impresora térmica está lista para usar'
        };
      case 'disconnected':
        return {
          icon: XCircle,
          color: 'text-amber-500',
          bg: 'bg-amber-50',
          text: 'QZ Tray no detectado',
          description: 'Instale QZ Tray para habilitar impresión directa'
        };
      case 'error':
        return {
          icon: AlertTriangle,
          color: 'text-red-500',
          bg: 'bg-red-50',
          text: 'Error de conexión',
          description: 'No se pudo conectar con QZ Tray'
        };
      default:
        return {
          icon: RefreshCw,
          color: 'text-blue-500',
          bg: 'bg-blue-50',
          text: 'Verificando...',
          description: 'Comprobando conexión con QZ Tray'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="printer-config-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 heading">Configuración de Impresora</h1>
          <p className="text-slate-500 mt-1">
            Configure la impresora térmica para tickets de turno
          </p>
        </div>

        {/* Status Card */}
        <Card className={statusInfo.bg}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${statusInfo.bg}`}>
                  <StatusIcon className={`w-8 h-8 ${statusInfo.color} ${status === 'checking' ? 'animate-spin' : ''}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{statusInfo.text}</h3>
                  <p className="text-sm text-slate-600">{statusInfo.description}</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={checkConnection}
                disabled={loading}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Verificar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* QZ Tray Installation */}
        {status === 'disconnected' && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Instalar QZ Tray
              </CardTitle>
              <CardDescription>
                QZ Tray es un software gratuito que permite imprimir directamente a impresoras térmicas desde el navegador
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Pasos de instalación:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
                  <li>Descargue QZ Tray desde <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">qz.io/download</a></li>
                  <li>Ejecute el instalador y siga las instrucciones</li>
                  <li>QZ Tray se iniciará automáticamente (icono en la bandeja del sistema)</li>
                  <li>Recargue esta página y haga clic en "Verificar"</li>
                  <li>La primera vez que imprima, deberá aprobar el certificado</li>
                </ol>
              </div>
              <Button 
                onClick={() => window.open('https://qz.io/download/', '_blank')}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar QZ Tray (Gratis)
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Printer Selection */}
        {status === 'connected' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Printer className="w-5 h-5" />
                Seleccionar Impresora
              </CardTitle>
              <CardDescription>
                Elija la impresora térmica que usará para los tickets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Impresora disponible</Label>
                <Select value={selectedPrinter} onValueChange={handleSelectPrinter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar impresora..." />
                  </SelectTrigger>
                  <SelectContent>
                    {printers.map((printer) => (
                      <SelectItem key={printer} value={printer}>
                        <span className="flex items-center gap-2">
                          <Printer className="w-4 h-4" />
                          {printer}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {printers.length === 0 && (
                  <p className="text-sm text-amber-600">
                    No se encontraron impresoras. Asegúrese de que la impresora esté conectada y encendida.
                  </p>
                )}
              </div>

              {selectedPrinter && (
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">Impresora seleccionada</p>
                      <p className="text-sm text-slate-600">{selectedPrinter}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Activa</Badge>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  onClick={loadPrinters}
                  variant="outline"
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualizar lista
                </Button>
                <Button
                  onClick={handleTestPrint}
                  disabled={!selectedPrinter || testPrinting}
                  className="gap-2"
                >
                  {testPrinting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                  {testPrinting ? 'Imprimiendo...' : 'Imprimir prueba'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Printer Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Información de la impresora térmica
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Ancho de papel:</strong> 80mm (estándar POS)</li>
              <li>• <strong>Conexión:</strong> USB directa al PC</li>
              <li>• <strong>Comandos:</strong> ESC/POS (compatible con la mayoría de impresoras)</li>
              <li>• <strong>Corte:</strong> Automático parcial después de cada ticket</li>
              <li>• <strong>Codificación:</strong> UTF-8 (soporta caracteres especiales)</li>
            </ul>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle>Solución de problemas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium text-slate-900">La impresora no aparece en la lista</h4>
                <p className="text-slate-600">
                  Verifique que la impresora esté conectada por USB, encendida, y que tenga los drivers instalados en Windows.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900">El ticket sale cortado o con caracteres extraños</h4>
                <p className="text-slate-600">
                  Asegúrese de usar papel de 80mm. Si el problema persiste, verifique que la impresora sea compatible con comandos ESC/POS.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900">QZ Tray pide aprobar un certificado</h4>
                <p className="text-slate-600">
                  Esto es normal la primera vez. Haga clic en "Permitir" o "Allow" para autorizar la impresión desde esta página.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PrinterConfigPage;
